/* eslint-disable no-console */
import { useClientProfile } from 'src/entities/client';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { useCallback, useState } from 'react';
import { ClientProfileViewModel } from '@common/model/client';
import { CareerId } from '@common/const/catalog/career';
import { clientTypeToSuitableCatalogItemType } from '@common/utils/catalogType';
import { AsyncResult, Result } from '@youhelp-ru/core';
import { logError, resetLoading, toError } from '../lib/result';
import { useResponseVacancyModals } from './responseVacancy';
import { useNavigateToDialog } from './useNavigateToDialog';

const namespace = 'VacancyResponse';

export interface VacancyResponseParams {
  responder?: ClientProfileViewModel;
}

export type VacancyResponseFeature = (catalogItemId: number, params?: VacancyResponseParams) => AsyncResult<void, Error>;

export const useVacancyResposne = (): [VacancyResponseFeature, boolean] => {
  const [isLoading, setIsLoading] = useState(false);

  const navigateToDialog = useNavigateToDialog(namespace);
  const openNannyModals = useResponseVacancyModals();

  const clientProfile = useClientProfile();
  const [queryResponse] = coreCatalogItemApi.endpoints.response.useMutation();
  const [queryCatalogItem] = coreCatalogItemApi.endpoints.getById.useLazyQuery();

  const proccessRunner = useCallback<VacancyResponseFeature>(
    async (catalogItemId, params) => {
      console.debug(`[${namespace}]: Starting the proccess with ${catalogItemId}...`);
      setIsLoading(true);

      const catalogItemResult = await Result.fromAsync(() => queryCatalogItem({ id: catalogItemId }).unwrap(), toError).then((result) =>
        result.flatMap((c) => {
          const Err = Result.Err<typeof c, Error>;
          const responder = params?.responder ?? clientProfile;

          if (!responder) {
            return Err(new Error('The responder was not found'));
          }

          if (!responder?.type || clientTypeToSuitableCatalogItemType(responder.type) !== c.type) {
            return Err(new Error('Invalid type of catalog item or client profile'));
          }

          return Result.Ok(c);
        }),
      );

      const connectionResult = await Result.fromAsync(() => queryResponse({ id: catalogItemId }).unwrap(), toError).then((result) =>
        result.map(({ dialogId }) => {
          const catalogItem = catalogItemResult.unwrap();
          return { catalogItem, dialogId };
        }),
      );

      const responseResult = await connectionResult.mapAsync(async ({ catalogItem, dialogId }) => {
        if (catalogItem.careerId === +CareerId.Nanny) {
          return openNannyModals(catalogItem, dialogId);
        }
        return navigateToDialog(dialogId);
      });

      return responseResult
        .tap(resetLoading(setIsLoading))
        .tap(() => console.debug(`[${namespace}]: The process was completed successfully!`))
        .tapError(resetLoading(setIsLoading))
        .tapError(logError(namespace));
    },
    [clientProfile, queryCatalogItem, queryResponse, openNannyModals, navigateToDialog],
  );

  return [proccessRunner, isLoading];
};
